import { useDeferredValue, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import { useActualizarCliente, useClientes, useCrearCliente } from '../api/queries';
import { DataNotice } from '../components/Badges';
import { PlusIcon, SearchIcon, UsersIcon, XIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { formatInteger } from '../lib/format';
import { normalizarRif, RIF_MENSAJE, rifValido } from '../lib/rif';
import type { Cliente } from '../api/types';
import { ResumenClienteModal } from '../components/ResumenTercero';
import { SortableTh } from '../components/SortableTh';
import { useTableSort } from '../lib/useTableSort';

const EMPTY_FORM = {
  rif: '',
  nombre: '',
  razonSocial: '',
  telefono: '',
  email: '',
  direccion: '',
  limiteCredito: '25000',
  diasCredito: '30',
  islrPct: '0',
};

export function ClientesPage() {
  const { user, demo } = useAuth();
  const { formatMoney, usdRate } = useCurrency();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [open, setOpen] = useState(false);
  const [resumenId, setResumenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const clientes = useClientes(deferredSearch);
  const crear = useCrearCliente();
  const actualizar = useActualizarCliente();

  const canCreate =
    !demo && user !== null && ['admin', 'supervisor', 'ventas'].includes(user.rol);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const rif = normalizarRif(form.rif);
    if (!rif || !form.nombre.trim()) {
      notify('RIF y nombre son obligatorios.', 'warning');
      return;
    }
    if (!rifValido(rif)) {
      notify(RIF_MENSAJE, 'warning');
      return;
    }
    const payload = {
      rif,
      nombre: form.nombre.trim(),
      ...(form.razonSocial.trim() ? { razonSocial: form.razonSocial.trim() } : {}),
      ...(form.telefono.trim() ? { telefono: form.telefono.trim() } : {}),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.direccion.trim() ? { direccion: form.direccion.trim() } : {}),
      limiteCredito: (Number(form.limiteCredito) || 0) / usdRate,
      diasCredito: Number(form.diasCredito) || 0,
      islrPct: Math.min(100, Math.max(0, Number(form.islrPct) || 0)),
    };
    try {
      if (editingId) {
        await actualizar.mutateAsync({ id: editingId, payload });
        notify(`Cliente ${form.nombre.trim()} actualizado.`, 'success');
      } else {
        await crear.mutateAsync(payload);
        notify(`Cliente ${form.nombre.trim()} creado.`, 'success');
      }
      setOpen(false);
      setEditingId(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear el cliente.', 'error');
    }
  };

  const rows = clientes.data?.data ?? [];
  const { rows: sortedRows, sort, toggleSort } = useTableSort<
    Cliente,
    'cliente' | 'contacto' | 'vendedor' | 'limite' | 'saldo' | 'dias' | 'estado'
  >(
    rows,
    {
      cliente: (row) => row.nombre,
      contacto: (row) => row.telefono ?? row.email,
      vendedor: (row) => row.vendedorNombre,
      limite: (row) => row.limiteCredito,
      saldo: (row) => row.saldoFavorUsd,
      dias: (row) => row.diasCredito,
      estado: (row) => row.activo,
    },
    'cliente',
    'asc',
  );
  const total = clientes.data?.meta.total ?? 0;

  const openEdit = (cliente: Cliente) => {
    setEditingId(cliente.id);
    setForm({
      rif: cliente.rif,
      nombre: cliente.nombre,
      razonSocial: cliente.razonSocial ?? '',
      telefono: cliente.telefono ?? '',
      email: cliente.email ?? '',
      direccion: cliente.direccion ?? '',
      limiteCredito: String(Math.round(cliente.limiteCredito * usdRate * 100) / 100),
      diasCredito: String(cliente.diasCredito),
      islrPct: String(cliente.islrPct ?? 0),
    });
    setOpen(true);
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Cartera comercial</p>
          <h1 className="page-title">Clientes & Cartera</h1>
          <p className="page-sub">
            Cartera asignada por vendedor, límites de crédito y estado de activación.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setEditingId(null);
            setForm(EMPTY_FORM);
            setOpen(true);
          }}
          disabled={!canCreate}
          title={canCreate ? undefined : demo ? 'No disponible en modo demo' : 'Tu rol no permite crear clientes'}
        >
          <PlusIcon size={17} />
          Nuevo cliente
        </button>
      </header>

      <DataNotice
        demo={demo}
        message={
          demo
            ? 'Cartera de demostración · el alta de clientes requiere conexión con el backend.'
            : undefined
        }
      />

      <section className="card clients-card">
        <div className="clients-toolbar">
          <div className="search-input">
            <SearchIcon size={17} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nombre o RIF…"
              aria-label="Buscar clientes"
            />
          </div>
          <span className="pill pill--soft">
            <UsersIcon size={14} />
            {formatInteger(total)} clientes
          </span>
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Cliente / RIF" sortKey="cliente" sort={sort} onSort={toggleSort} />
                <SortableTh label="Contacto" sortKey="contacto" sort={sort} onSort={toggleSort} />
                <SortableTh label="Vendedor" sortKey="vendedor" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="Límite crédito" sortKey="limite" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="Saldo a favor" sortKey="saldo" sort={sort} onSort={toggleSort} />
                <SortableTh label="Días" sortKey="dias" sort={sort} onSort={toggleSort} />
                <SortableTh label="Estado" sortKey="estado" sort={sort} onSort={toggleSort} />
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {clientes.isLoading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="table-empty">Cargando cartera…</div>
                  </td>
                </tr>
              ) : null}
              {!clientes.isLoading && clientes.isError ? (
                <tr>
                  <td colSpan={8}>
                    <div className="table-empty">
                      <p>No se pudo cargar la cartera.</p>
                      <button
                        type="button"
                        className="btn btn--outline btn--sm"
                        onClick={() => void clientes.refetch()}
                      >
                        Reintentar
                      </button>
                    </div>
                  </td>
                </tr>
              ) : null}
              {!clientes.isLoading && !clientes.isError && rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="table-empty">
                      <p>No hay clientes que coincidan con la búsqueda.</p>
                    </div>
                  </td>
                </tr>
              ) : null}
              {sortedRows.map((cliente) => (
                <tr key={cliente.id} className="row-clickable" onClick={() => setResumenId(cliente.id)}>
                  <td>
                    <div className="doc-cell">
                      <strong>{cliente.nombre}</strong>
                      <span>{cliente.rif}</span>
                    </div>
                  </td>
                  <td>
                    <div className="doc-cell">
                      <strong>{cliente.telefono ?? 'Sin teléfono'}</strong>
                      <span>{cliente.direccion ?? 'Sin dirección'}</span>
                    </div>
                  </td>
                  <td>{cliente.vendedorNombre ?? '—'}</td>
                  <td className="align-right">{formatMoney(cliente.limiteCredito)}</td>
                  <td className="align-right">{formatMoney(cliente.saldoFavorUsd ?? 0)}</td>
                  <td>{cliente.diasCredito} días</td>
                  <td>
                    <span className={`status-badge status-badge--${cliente.activo ? 'success' : 'neutral'}`}>
                      <span className="status-badge__dot" aria-hidden="true" />
                      {cliente.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={!canCreate}
                      onClick={(event) => {
                        event.stopPropagation();
                        openEdit(cliente);
                      }}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {resumenId ? <ResumenClienteModal clienteId={resumenId} onClose={() => setResumenId(null)} /> : null}

      {open ? createPortal(
        <div
          className="modal-backdrop modal-backdrop--center"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="client-title">
            <header className="modal-card__head">
              <div>
                <p className="eyebrow">Cartera</p>
                <h2 id="client-title">{editingId ? 'Editar cliente' : 'Nuevo cliente'}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => { setOpen(false); setEditingId(null); }}
                aria-label="Cerrar formulario"
              >
                <XIcon size={20} />
              </button>
            </header>
            <form onSubmit={(event) => void submit(event)}>
              <div className="modal-card__body">
                <div className="form-grid">
                  <label className="field">
                    <span className="field-label">RIF *</span>
                    <input
                      className="input"
                      value={form.rif}
                      onChange={(event) => setForm({ ...form, rif: event.target.value })}
                      onBlur={(event) => setForm({ ...form, rif: normalizarRif(event.target.value) })}
                      placeholder="J-40123456-0"
                      maxLength={12}
                      required
                    />
                    <span className="field-help">Formato J-12345678-9</span>
                  </label>
                  <label className="field">
                    <span className="field-label">Nombre comercial *</span>
                    <input
                      className="input"
                      value={form.nombre}
                      onChange={(event) => setForm({ ...form, nombre: event.target.value })}
                      placeholder="Agropecuaria Los Llanos"
                      required
                    />
                  </label>
                  <label className="field field--wide">
                    <span className="field-label">Razón social</span>
                    <input
                      className="input"
                      value={form.razonSocial}
                      onChange={(event) => setForm({ ...form, razonSocial: event.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Teléfono</span>
                    <input
                      className="input"
                      value={form.telefono}
                      onChange={(event) => setForm({ ...form, telefono: event.target.value })}
                      placeholder="+58 412-0000000"
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Correo</span>
                    <input
                      className="input"
                      type="email"
                      value={form.email}
                      onChange={(event) => setForm({ ...form, email: event.target.value })}
                    />
                  </label>
                  <label className="field field--wide">
                    <span className="field-label">Dirección</span>
                    <input
                      className="input"
                      value={form.direccion}
                      onChange={(event) => setForm({ ...form, direccion: event.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Límite de crédito (Bs.)</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="100"
                      value={form.limiteCredito}
                      onChange={(event) => setForm({ ...form, limiteCredito: event.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Días de crédito</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="180"
                      value={form.diasCredito}
                      onChange={(event) => setForm({ ...form, diasCredito: event.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">ISLR que retiene (%)</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={form.islrPct}
                      onChange={(event) => setForm({ ...form, islrPct: event.target.value })}
                    />
                    <span className="field-hint">
                      0 si el cliente no retiene; se sugiere al registrar la cobranza.
                    </span>
                  </label>
                </div>
              </div>
              <footer className="modal-card__foot">
                <button type="button" className="btn btn--outline" onClick={() => { setOpen(false); setEditingId(null); }}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn--primary" disabled={crear.isPending}>
                  {crear.isPending || actualizar.isPending ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear cliente'}
                </button>
              </footer>
            </form>
          </section>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
